import React from 'react';
import { FaExclamationCircle, FaInfoCircle, FaCheckCircle, FaTimes } from 'react-icons/fa';

const ErrorMessage = ({ 
  type = 'error', 
  title, 
  message, 
  details, 
  onRetry, 
  onDismiss, 
  showDismiss = true,
  className = '' 
}) => {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return <FaExclamationCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <FaExclamationCircle className="h-5 w-5 text-yellow-500" />;
      case 'info':
        return <FaInfoCircle className="h-5 w-5 text-blue-500" />;
      case 'success':
        return <FaCheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <FaExclamationCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-red-50 border-red-200';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      case 'success':
        return 'text-green-800';
      default:
        return 'text-red-800';
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${getBackgroundColor()} ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        
        <div className="ml-3 flex-1">
          {title && (
            <h3 className={`text-sm font-medium ${getTextColor()}`}>
              {title}
            </h3>
          )}
          
          {message && (
            <div className={`mt-1 text-sm ${getTextColor()}`}>
              {message}
            </div>
          )}
          
          {details && (
            <details className="mt-2">
              <summary className={`text-xs cursor-pointer ${getTextColor()} hover:underline`}>
                Show details
              </summary>
              <div className={`mt-2 text-xs font-mono bg-white bg-opacity-50 p-2 rounded ${getTextColor()}`}>
                {details}
              </div>
            </details>
          )}
          
          {onRetry && (
            <div className="mt-3">
              <button
                onClick={onRetry}
                className={`text-sm font-medium ${getTextColor()} hover:underline`}
              >
                Try again
              </button>
            </div>
          )}
        </div>
        
        {showDismiss && onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                onClick={onDismiss}
                className={`inline-flex rounded-md p-1.5 ${getTextColor()} hover:bg-white hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600`}
              >
                <span className="sr-only">Dismiss</span>
                <FaTimes className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Predefined error messages for common scenarios
export const ErrorMessages = {
  // File upload errors
  FILE_TOO_LARGE: {
    type: 'error',
    title: 'File too large',
    message: 'The file is too large. Please choose a file smaller than 100MB.'
  },
  
  INVALID_FILE_TYPE: {
    type: 'error',
    title: 'Invalid file type',
    message: 'Please upload only PDF or PowerPoint (.pptx) files.'
  },
  
  FILE_CORRUPTED: {
    type: 'error',
    title: 'File appears to be corrupted',
    message: 'We couldn\'t read this file. Please try a different file or re-save your document.'
  },
  
  // Network errors
  NETWORK_ERROR: {
    type: 'error',
    title: 'Connection problem',
    message: 'Unable to connect to the server. Please check your internet connection and try again.'
  },
  
  UPLOAD_FAILED: {
    type: 'error',
    title: 'Upload failed',
    message: 'Failed to upload your files. Please try again or contact support if the problem persists.'
  },
  
  // Payment errors
  PAYMENT_FAILED: {
    type: 'error',
    title: 'Payment failed',
    message: 'Your payment could not be processed. Please try again or use a different payment method.'
  },
  
  PAYMENT_VERIFICATION_FAILED: {
    type: 'error',
    title: 'Payment verification failed',
    message: 'We couldn\'t verify your payment. Please contact support with your payment reference.'
  },
  
  // Database errors
  SAVE_FAILED: {
    type: 'error',
    title: 'Save failed',
    message: 'Failed to save your data. Please try again.'
  },
  
  FETCH_FAILED: {
    type: 'error',
    title: 'Unable to load data',
    message: 'We couldn\'t load the requested information. Please refresh the page and try again.'
  },
  
  // Authentication errors
  AUTH_REQUIRED: {
    type: 'warning',
    title: 'Authentication required',
    message: 'Please sign in to continue.'
  },
  
  SESSION_EXPIRED: {
    type: 'warning',
    title: 'Session expired',
    message: 'Your session has expired. Please sign in again.'
  },
  
  // Success messages
  UPLOAD_SUCCESS: {
    type: 'success',
    title: 'Files uploaded successfully',
    message: 'Your files have been uploaded and are ready for printing.'
  },
  
  PAYMENT_SUCCESS: {
    type: 'success',
    title: 'Payment successful',
    message: 'Your payment has been processed successfully.'
  },
  
  SAVE_SUCCESS: {
    type: 'success',
    title: 'Changes saved',
    message: 'Your changes have been saved successfully.'
  }
};

export default ErrorMessage;
